"use client"

import {
    Pagination as IPagination,
    PaginationContent,
    PaginationEllipsis,
    PaginationItem,
    PaginationLink,
    PaginationNext,
    PaginationPrevious,
} from "../ui/pagination"
import { buildUrl, cn } from "../../lib/utils"
import { usePathname, useSearchParams } from "next/navigation"
import { usePaginationData } from "../../context/pagination-data-context"

function Pagination() {

    const pathname = usePathname()
    const searchParams = useSearchParams()

    const { paginationData } = usePaginationData()

    const { page, limit, total } = paginationData

    const totalPages = Math.ceil(total / limit)

    // Generate page links with proper URLs
    const getPageUrl = (pageNumber: number) => {
        // Build params object, handling multiple values for the same key
        const params: Record<string, string | string[]> = {}
        
        // Get all unique keys from searchParams
        const keys = Array.from(searchParams.keys()).filter((key, index, self) => 
            self.indexOf(key) === index
        )
        
        // For each key, get all values and store as array if multiple, or string if single
        keys.forEach(key => {
            const values = searchParams.getAll(key)
            params[key] = values.length > 1 ? values : values[0]
        })
        
        return buildUrl(pathname, {
            ...params,
            page: pageNumber,
        });
    }

    // Create array of page numbers to display
    const getPageNumbers = () => {
        const pages = []
        const maxPagesToShow = 5

        if (totalPages <= maxPagesToShow) {
            // Show all pages if total pages are less than max to show
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i)
            }
        } else {
            // Always show first page
            pages.push(1)

            // Calculate start and end of pages to show around current page
            let startPage = Math.max(2, page - 1)
            let endPage = Math.min(totalPages - 1, page + 1)

            // Adjust to show max visible pages
            if (endPage - startPage < maxPagesToShow - 3) {
                if (startPage === 2) {
                    endPage = Math.min(totalPages - 1, maxPagesToShow - 2)
                } else if (endPage === totalPages - 1) {
                    startPage = Math.max(2, totalPages - maxPagesToShow + 2)
                }
            }

            // Add ellipsis before middle pages if needed
            if (startPage > 2) {
                pages.push('ellipsis-start')
            }

            // Add middle pages
            for (let i = startPage; i <= endPage; i++) {
                pages.push(i)
            }

            // Add ellipsis after middle pages if needed
            if (endPage < totalPages - 1) {
                pages.push('ellipsis-end')
            }

            // Always show last page
            if (totalPages > 1) {
                pages.push(totalPages)
            }
        }

        return pages
    }

    // Hide pagination if total is not available
    if (!total) return null

    if(totalPages <= 1) return null

    return (
        <IPagination>
            <PaginationContent>
                <PaginationItem>
                    <PaginationPrevious
                        href={page > 1 ? getPageUrl(page - 1) : "#"}
                        size={"sm"}
                        aria-disabled={page <= 1}
                        className={cn({
                            "pointer-events-none text-muted-foreground": page <= 1
                        })}
                    />
                </PaginationItem>

                {getPageNumbers().map((pageNum, index) => {
                    if (pageNum === 'ellipsis-start' || pageNum === 'ellipsis-end') {
                        return (
                            <PaginationItem key={`ellipsis-${index}`}>
                                <PaginationEllipsis />
                            </PaginationItem>
                        )
                    }

                    return (
                        <PaginationItem key={pageNum}>
                            <PaginationLink
                                href={getPageUrl(pageNum as number)}
                                isActive={page === pageNum}
                            >
                                {pageNum}
                            </PaginationLink>
                        </PaginationItem>
                    )
                })}

                <PaginationItem>
                    <PaginationNext
                        href={page < totalPages ? getPageUrl(page + 1) : "#"}
                        size={"sm"}
                        aria-disabled={page >= totalPages}
                        className={cn({
                            "pointer-events-none opacity-50": page >= totalPages
                        })}
                    />
                </PaginationItem>
            </PaginationContent>
        </IPagination>
    )
}

export default Pagination